import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { analyticsAttributionParams } from "@/lib/leadAttribution";

// Extend Window interface for GTM
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

interface TrackingEventParams {
  eventCategory?: string;
  eventLabel?: string;
  eventValue?: number;
  funnelPath?: string;
  toolId?: string;
  variantId?: string;
  variantName?: string;
  [key: string]: unknown;
}

interface AnalyticsHook {
  trackEvent: (eventName: string, params?: TrackingEventParams) => void;
  trackPageView: (pagePath?: string, pageTitle?: string) => void;
  trackFormSubmit: (formId: string, formData?: Record<string, unknown>) => void;
  trackCTAClick: (ctaId: string, ctaText: string, destination?: string) => void;
  trackLeadCapture: (leadType: string, value?: number) => void;
  trackScrollDepth: (depth: number) => void;
  trackDemoInteraction: (demoId: string, action: string) => void;
  trackConversion: (conversionType: string, value?: number, transactionId?: string) => void;
}

// Push to GTM dataLayer
const pushToDataLayer = (event: string, params: Record<string, unknown> = {}) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event,
      ...params,
      timestamp: new Date().toISOString(),
    });
  }
};

const enrichWithAttribution = (params: Record<string, unknown> = {}) => ({
  ...analyticsAttributionParams(),
  ...params,
});

export const useAnalytics = (): AnalyticsHook => {
  const location = useLocation();

  const trackEvent = useCallback(
    (eventName: string, params: TrackingEventParams = {}) => {
      const enrichedParams = {
        ...analyticsAttributionParams(),
        ...params,
        page_path: location.pathname,
        page_url: window.location.href,
      };

      // Push to GTM
      pushToDataLayer(eventName, enrichedParams);

      // Console log in development
      if (import.meta.env.DEV) {
        console.log("[Analytics]", eventName, enrichedParams);
      }
    },
    [location.pathname]
  );

  const trackPageView = useCallback(
    (pagePath?: string, pageTitle?: string) => {
      const path = pagePath || location.pathname;
      const title = pageTitle || document.title;

      pushToDataLayer("page_view", {
        ...analyticsAttributionParams(),
        page_path: path,
        page_title: title,
        page_url: window.location.href,
      });
    },
    [location.pathname]
  );

  const trackFormSubmit = useCallback(
    (formId: string, formData?: Record<string, unknown>) => {
      const params = {
        ...analyticsAttributionParams(),
        form_id: formId,
        form_data: formData,
        page_path: location.pathname,
      };

      pushToDataLayer("form_submit", params);
    },
    [location.pathname]
  );

  const trackCTAClick = useCallback(
    (ctaId: string, ctaText: string, destination?: string) => {
      const params = {
        ...analyticsAttributionParams(),
        cta_id: ctaId,
        cta_text: ctaText,
        cta_destination: destination,
        page_path: location.pathname,
      };

      pushToDataLayer("cta_click", params);
    },
    [location.pathname]
  );

  const trackLeadCapture = useCallback(
    (leadType: string, value?: number) => {
      const params = {
        ...analyticsAttributionParams(),
        lead_type: leadType,
        lead_value: value,
        page_path: location.pathname,
      };

      pushToDataLayer("lead_captured", params);
    },
    [location.pathname]
  );

  const trackScrollDepth = useCallback(
    (depth: number) => {
      pushToDataLayer("scroll_depth", {
        ...analyticsAttributionParams(),
        scroll_percentage: depth,
        page_path: location.pathname,
      });
    },
    [location.pathname]
  );

  const trackDemoInteraction = useCallback(
    (demoId: string, action: string) => {
      const params = {
        ...analyticsAttributionParams(),
        demo_id: demoId,
        demo_action: action,
        page_path: location.pathname,
      };

      pushToDataLayer("demo_interaction", params);
    },
    [location.pathname]
  );

  const trackConversion = useCallback(
    (conversionType: string, value?: number, transactionId?: string) => {
      const params = {
        ...analyticsAttributionParams(),
        conversion_type: conversionType,
        conversion_value: value,
        transaction_id: transactionId,
        page_path: location.pathname,
      };

      pushToDataLayer("conversion", params);
    },
    [location.pathname]
  );

  return {
    trackEvent,
    trackPageView,
    trackFormSubmit,
    trackCTAClick,
    trackLeadCapture,
    trackScrollDepth,
    trackDemoInteraction,
    trackConversion,
  };
};

// Standalone tracking functions for use outside React components
export const trackEventStandalone = (
  eventName: string,
  params: Record<string, unknown> = {}
) => {
  const enrichedParams = enrichWithAttribution(params);

  if (eventName === "lead_captured") {
    pushToDataLayer("form_submit", {
      ...enrichedParams,
      form_id: params.form_id || params.lead_type || "lead_capture",
      conversion_event: "lead_captured",
    });
  }

  pushToDataLayer(eventName, enrichedParams);
};

export default useAnalytics;
